import { useApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  CheckboxField,
  Divider,
  EmptyStation,
  type FormActionData,
  formatDateTime,
  FormStation,
  getFormDiff,
  InfoPanel,
  MessageBar,
  type ObjectSchemaDefinition,
  Paragraph,
  RadioField,
  ReadOnlyField,
  Section,
  SingleLineTextField,
  type StationMessage,
} from '@axinom/mosaic-ui';
import { Field, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
  PLACEHOLDER_BUILT_IN_GOOGLE_IDP_CLIENT_ID,
  PLACEHOLDER_BUILT_IN_GOOGLE_IDP_CLIENT_SECRET,
} from '../../../../common/constants/idp-constants';
import {
  IdentityProvider,
  type IdpConfiguration,
  type IdpConfigurationsQuery,
  useDeleteIdpConfigurationMutation,
  useIdpConfigurationsQuery,
  useUpdateIdpConfigurationMutation,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import {
  type CustomBrandingConfig,
  customBrandingValidation,
  CustomIdpBranding,
} from '../IdentityProviders/CustomIdpBranding/CustomIdpBranding';

interface FormData extends CustomBrandingConfig {
  id?: string;
  discoveryDocumentUrl?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
  clientSecretIsSet?: boolean;
  useBuiltInIdp?: boolean | null;
}

type IdpConfigurationData = NonNullable<
  IdpConfigurationsQuery['configurations']
>['nodes'][number];

const getIdpConfigurationSchema = (
  isNewRecord: boolean,
): Yup.ObjectSchema<ObjectSchemaDefinition> => {
  return Yup.object<ObjectSchemaDefinition>({
    useBuiltInIdp: Yup.boolean().label('Use Built-In IDP'),
    clientId: Yup.string()
      .label('Client ID')
      .max(255)
      .when('useBuiltInIdp', {
        is: false,
        then: Yup.string().nullable(),
      })
      .test(
        'client-id-is-mandatory',
        'Client ID is required when the IDP is enabled',
        function (value) {
          const enabled = this.parent.enabled;
          if (enabled && !value) {
            return false;
          }
          return true;
        },
      ),
    clientSecret: Yup.string()
      .label('Client Secret')
      .max(255)
      .when(['useBuiltInIdp'], {
        is: (useBuiltInIdp: boolean) => isNewRecord && useBuiltInIdp === false,
        then: Yup.string(),
        otherwise: Yup.string().nullable(),
      })
      .test(
        'client-secret-is-mandatory',
        'Client Secret is required when the IDP is enabled',
        function (value) {
          const enabled = this.parent.enabled;
          const isSecretSet = this.parent.clientSecretIsSet;
          if (enabled && !isSecretSet && !value) {
            return false;
          }
          return true;
        },
      ),
  }).concat(customBrandingValidation);
};

const placeholderBuiltInGoogleIdpClientId =
  PLACEHOLDER_BUILT_IN_GOOGLE_IDP_CLIENT_ID;
const placeholderBuiltInGoogleIdpClientSecret =
  PLACEHOLDER_BUILT_IN_GOOGLE_IDP_CLIENT_SECRET;

export const IdpGoogleDetail: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const apolloClient = useApolloClient();

  // Check the status of id-service
  useEffect(() => {
    (async () => {
      const statusMessage = await getManagedServiceAvailabilityMessage(
        ManagedServiceId.ID_SERVICE,
        envId,
      );
      setServiceStatusMessage(statusMessage);
    })();
  }, [envId]);

  const { loading, data, error } = useIdpConfigurationsQuery({
    client: apolloClient,
    variables: {
      idpConfigurationCondition: {
        environmentId: envId,
        idpId: IdentityProvider.Google,
      },
      idpScopeFilter: {
        idpId: {
          equalTo: IdentityProvider.Google,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const [updateIdpConfig] = useUpdateIdpConfigurationMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const idpConfiguration = data?.configurations?.nodes[0];
  const detailObj: FormData = idpConfiguration || {
    useBuiltInIdp: false,
  };

  const { actions } = useActions(envId, detailObj as IdpConfiguration);

  const onSubmit = useCallback(
    async (formData: FormData, initialData): Promise<void> => {
      if (initialData.loading) {
        return;
      }

      await updateIdpConfig({
        variables: {
          input: {
            id: initialData.data?.id,
            patch: createUpdateDto(formData, initialData.data),
          },
        },
      });
    },
    [updateIdpConfig],
  );

  const [message] = useState<StationMessage>({
    title:
      'Certain Identity Providers may have different policies for automatically expiring Client Secrets. Please ensure if/when such expiration happens, the values stored here will be updated in a timely manner to provide an uninterrupted authentication experience.',
    type: 'info',
  });

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="Google Identity Provider" subtitle="Configuration">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <FormStation
          defaultTitle="Google Identity Provider"
          subtitle="Configuration"
          actions={idpConfiguration === undefined ? [] : actions}
          validationSchema={getIdpConfigurationSchema(!idpConfiguration)}
          initialData={{
            data: detailObj,
            loading,
            error: error?.message,
          }}
          saveData={onSubmit}
          infoPanel={idpConfiguration ? <Panel /> : undefined}
          stationMessage={message}
        >
          <Form isNew={!idpConfiguration} />
        </FormStation>
      )}
    </>
  );
};

const Panel: React.FC = () => {
  const { values } = useFormikContext<IdpConfiguration>();

  return (
    <InfoPanel>
      <Section>
        <Paragraph title="Last Modified">
          {formatDateTime(values.updatedAt)} by {values.updatedBy}
        </Paragraph>
      </Section>
      <Section>
        <Paragraph title="Tenant ID">{values.tenantId}</Paragraph>
        <Paragraph title="Environment ID">{values.environmentId}</Paragraph>
      </Section>
    </InfoPanel>
  );
};

const Form: React.FC<{
  isNew: boolean;
}> = ({ isNew }) => {
  const { values, setFieldValue, dirty } =
    useFormikContext<IdpConfigurationData>();

  useEffect(() => {
    if (dirty === true) {
      if (values.useBuiltInIdp === true) {
        setFieldValue('clientId', placeholderBuiltInGoogleIdpClientId);
        setFieldValue('clientSecret', placeholderBuiltInGoogleIdpClientSecret);
      } else if (values.useBuiltInIdp === false) {
        setFieldValue('clientId', null);
        setFieldValue('clientSecret', null);
        setFieldValue('clientSecretIsSet', false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.useBuiltInIdp]);

  return (
    <>
      {!isNew && (
        <>
          <Field
            name="enabled"
            label="IDP Status"
            options={[
              { value: true, label: 'Enabled' },
              { value: false, label: 'Disabled' },
            ]}
            transform={(value: string) => JSON.parse(value)}
            as={RadioField}
          />
          <Divider />
        </>
      )}

      <Field
        name="useBuiltInIdp"
        label="Use Built-In IDP"
        tooltipContent="Use Axinom pre-configured Google IDP Configuration"
        as={CheckboxField}
      />

      {!values.useBuiltInIdp && (
        <>
          <Field
            name="clientId"
            label="Client ID"
            autoComplete="off"
            as={SingleLineTextField}
          />
          <Field
            name="clientSecret"
            label="Client Secret"
            type="password"
            autoComplete="one-time-code"
            isSet={values?.clientSecretIsSet}
            as={SingleLineTextField}
          />
          <Field
            name="discoveryDocumentUrl"
            label="Discovery Document URL"
            as={ReadOnlyField}
          />
        </>
      )}
      <Divider />
      <CustomIdpBranding />
    </>
  );
};

const useActions = (
  envId: string,
  idp: IdpConfiguration,
): {
  readonly actions: FormActionData[];
} => {
  const apolloClient = useApolloClient();
  const history = useHistory();
  const [idpDelete] = useDeleteIdpConfigurationMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const actions: FormActionData[] = [
    {
      label: 'Delete Configuration',
      confirmationMode: 'Simple',
      onActionSelected: async () => {
        await idpDelete({ variables: { input: { id: idp.id } } });
        history.push(
          `/env/${envId}/services/${ManagedServiceId.ID_SERVICE}/config/identityProviders`,
        );
      },
    },
  ];

  return { actions } as const;
};

function createUpdateDto(
  currentValues: FormData,
  initialValues: FormData | null | undefined,
): Partial<FormData> {
  const updateData = getFormDiff(currentValues, initialValues);
  delete updateData.clientSecretIsSet;

  updateData.clientId = updateData.clientId === '' ? null : updateData.clientId;
  updateData.clientSecret =
    updateData.clientSecret === '' ? null : updateData.clientSecret;

  return updateData;
}

export const GoogleProviderDetailCrumb: BreadcrumbResolver = () => 'Google IDP';
